import React from "react";
import { enableKeyIsValid } from "@aidkitorg/types/lib/eval";
import { Card, CardBody, Heading, Badge } from "@aidkitorg/component-library";
import type { NotificationResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/notificationsWorkflow";
import WorkflowReview from "./WorkflowReview";

// NotificationView component handles workflow display and status polling
export default function NotificationsWorkflowReview({ distroCommitId }: { distroCommitId: string }) {
  const getNotificationTitle = (notification: any) => {
    if (!notification) return 'Unknown Notification';
    return notification.name || notification.targetPrefix || 'Unnamed Notification';
  };

  const getCountDifference = (newCount: number, oldCount: number) => {
    const diff = newCount - oldCount;
    if (diff === 0) return null;
    return diff > 0 ? `+${diff}` : `-${diff}`;
  };

  const hasError = (surveyResult: NotificationResult['newSurveyResult'] | NotificationResult['oldSurveyResult']) => {
    return surveyResult && 'error' in surveyResult;
  };

  const getEnablementStatus = (notification: NotificationResult['newNotification'] | NotificationResult['oldNotification']) => {
    if (!notification) return null;
    
    try {
      const isEnabled = enableKeyIsValid(notification.enableKey, notification, { allowVersions: ['legacy']});
      if (isEnabled) return 'enabled';
      if (notification.enableKey) return 'unknown'; // outdated keys may cause false negatives, so just show as unknown
      return 'disabled';
    } catch (error) {
      return 'unknown';
    }
  };

  const renderEnablementBadge = (result: NotificationResult['newSurveyResult'] | NotificationResult['oldSurveyResult'] | null) => {
    if (!result) return null;
    if ('error' in result) return <Badge variant="error" className="text-xs">error</Badge>;
    if (result.enabled === undefined) return <Badge variant="warning" className="text-xs">unknown</Badge>;
    
    return (
      <Badge variant={result.enabled ? 'success' : 'error'} className="text-xs">
        {result.enabled ? 'Enabled' : 'Disabled'}
      </Badge>
    );
  };

  const renderNotificationResults = (workflow: any) => {
    if (!workflow?.result?.success || !workflow.result.notificationResults) {
      return null;
    }

    const results = workflow.result.notificationResults as NotificationResult[];

    // Categorize results
    const newNotifications = results.filter((r: NotificationResult) => r.changeType === 'new');
    const removedNotifications = results.filter((r: NotificationResult) => r.changeType === 'removed');
    const changedNotifications = results.filter((r: NotificationResult) => r.changeType === 'changed');
    const unchangedNotifications = results.filter((r: NotificationResult) => r.changeType === 'unchanged');
    const errorNotifications = results.filter((r: NotificationResult) => 
      hasError(r.newSurveyResult) || hasError(r.oldSurveyResult)
    );

    // Calculate summary
    const summary = {
      totalNotifications: results.length,
      newNotifications: newNotifications.length,
      removedNotifications: removedNotifications.length,
      changedNotifications: changedNotifications.length,
      unchangedNotifications: unchangedNotifications.length,
      errorCount: errorNotifications.length
    };

    return (
      <div className="space-y-6">
        {/* Summary */}
        <Card>
          <CardBody>
            <Heading level="h3" className="text-md font-semibold mb-3">Summary</Heading>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium">Total Notifications</div>
                <div>{summary.totalNotifications}</div>
              </div>
              {summary.newNotifications > 0 && (
                <div>
                  <div className="font-medium text-green-600">New Notifications</div>
                  <div>{summary.newNotifications}</div>
                </div>
              )}
              {summary.removedNotifications > 0 && (
                <div>
                  <div className="font-medium text-red-600">Removed Notifications</div>
                  <div>{summary.removedNotifications}</div>
                </div>
              )}
              {summary.changedNotifications > 0 && (
                <div>
                  <div className="font-medium text-yellow-600">Changed Notifications</div>
                  <div>{summary.changedNotifications}</div>
                </div>
              )}
              {summary.errorCount > 0 && (
                <div>
                  <div className="font-medium text-red-600">Processing Errors</div>
                  <div>{summary.errorCount}</div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* New Notifications */}
        {newNotifications.length > 0 && (
          <Card>
            <CardBody>
              <Heading level="h3" className="text-md font-semibold mb-3">
                New Notifications ({newNotifications.length})
              </Heading>
              <div className="space-y-2">
                {newNotifications.map((result: NotificationResult, index: number) => (
                  <div key={index} className="border-l-4 border-green-500 pl-3 py-2">
                    <div className="font-medium flex items-center gap-2">
                      {getNotificationTitle(result.newNotification)}
                      {renderEnablementBadge(result.newSurveyResult)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {(result.newSurveyResult && 'error' in result.newSurveyResult) ? (
                        <span className="text-red-600">Error: {(result.newSurveyResult as any).error}</span>
                      ) : (
                        <>
                          Will notify {result.newSurveyResult?.count ?? 0} applicants
                          {(result.newSurveyResult?.yetToReceiveCount ?? 0) > 0 && 
                            ` (${result.newSurveyResult?.yetToReceiveCount ?? 0} yet to receive)`
                          }
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Removed Notifications */}
        {removedNotifications.length > 0 && (
          <Card>
            <CardBody>
              <Heading level="h3" className="text-md font-semibold mb-3">
                Removed Notifications ({removedNotifications.length})
              </Heading>
              <div className="space-y-2">
                {removedNotifications.map((result: NotificationResult, index: number) => (
                  <div key={index} className="border-l-4 border-red-500 pl-3 py-2">
                    <div className="font-medium flex items-center gap-2">
                      {getNotificationTitle(result.oldNotification)}
                      {renderEnablementBadge(result.oldSurveyResult)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {(result.oldSurveyResult && 'error' in result.oldSurveyResult) ? (
                        <span className="text-red-600">Error: {(result.oldSurveyResult as any).error}</span>
                      ) : (
                        `Previously notified ${result.oldSurveyResult?.count ?? 0} applicants`
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Changed Notifications */}
        {changedNotifications.length > 0 && (
          <Card>
            <CardBody>
              <Heading level="h3" className="text-md font-semibold mb-3">
                Changed Notifications ({changedNotifications.length})
              </Heading>
              <div className="space-y-2">
                {changedNotifications.map((result: NotificationResult, index: number) => {
                  const newCount = result.newSurveyResult && 'count' in result.newSurveyResult ? result.newSurveyResult.count : 0;
                  const oldCount = result.oldSurveyResult && 'count' in result.oldSurveyResult ? result.oldSurveyResult.count : 0;
                  const newYetToReceive = result.newSurveyResult && 'yetToReceiveCount' in result.newSurveyResult ? result.newSurveyResult.yetToReceiveCount : 0;
                  const oldYetToReceive = result.oldSurveyResult && 'yetToReceiveCount' in result.oldSurveyResult ? result.oldSurveyResult.yetToReceiveCount : 0;
                  
                  const countDiff = getCountDifference(newCount, oldCount);
                  const yetToReceiveDiff = getCountDifference(newYetToReceive, oldYetToReceive);
                  const isCountIncrease = newCount > oldCount;
                  const isYetToReceiveIncrease = newYetToReceive > oldYetToReceive;
                  
                  
                  
                  return (
                    <div key={index} className="border-l-4 border-yellow-500 pl-3 py-2">
                      <div className="font-medium flex items-center gap-2">
                        {getNotificationTitle(result.newNotification || result.oldNotification)}
                        <>
                          {renderEnablementBadge(result.oldSurveyResult)} → {renderEnablementBadge(result.newSurveyResult)}
                        </>
                      </div>
                      <div className="text-sm text-gray-600">
                        {hasError(result.newSurveyResult) || hasError(result.oldSurveyResult) ? (
                          <div className="text-sm text-red-600">
                            {hasError(result.oldSurveyResult) && `Old: ${(result.oldSurveyResult as any).error}`}
                            <br />
                            {hasError(result.newSurveyResult) && `New: ${(result.newSurveyResult as any).error}`}
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div>
                              <span className="mr-4">
                                {oldCount} → {newCount} applicants
                              </span>
                              {countDiff && (
                                <span className={`font-medium ${isCountIncrease ? 'text-green-600' : 'text-red-600'}`}>
                                  ({countDiff})
                                </span>
                              )}
                            </div>
                            {(newYetToReceive > 0 || oldYetToReceive > 0) && (
                              <div>
                                <span className="mr-4">
                                  {oldYetToReceive} → {newYetToReceive} yet to receive
                                </span>
                                {yetToReceiveDiff && (
                                  <span className={`font-medium ${isYetToReceiveIncrease ? 'text-green-600' : 'text-red-600'}`}>
                                    ({yetToReceiveDiff})
                                  </span>
                                )}
                              </div>
                            )}

                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Unchanged Notifications */}
        {unchangedNotifications.length > 0 && (
          <Card>
            <CardBody>
              <Heading level="h3" className="text-md font-semibold mb-3">
                Unchanged Notifications ({unchangedNotifications.length})
              </Heading>
              <div className="space-y-1">
                {unchangedNotifications.map((result: NotificationResult, index: number) => (
                  <div key={index} className="border-l-4 border-gray-300 pl-3 py-1">
                    <div className="text-sm flex items-center gap-2">
                      <span className="font-medium">{getNotificationTitle(result.newNotification || result.oldNotification)}</span>
                      {renderEnablementBadge(result.newSurveyResult)}
                      <span className="text-gray-600">
                        { (result.newSurveyResult && 'error' in result.newSurveyResult) ? (
                          <span className="text-red-600">Error: {(result.newSurveyResult as any).error}</span>
                        ) : (
                          <>
                            {result.newSurveyResult && 'count' in result.newSurveyResult ? result.newSurveyResult.count : 0} applicants
                            {(result.newSurveyResult && 'yetToReceiveCount' in result.newSurveyResult && result.newSurveyResult.yetToReceiveCount > 0) &&
                              ` (${result.newSurveyResult.yetToReceiveCount} yet to receive)`
                            }
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    );
  };

  return (
    <WorkflowReview
      distroCommitId={distroCommitId}
      workflowName="notifications"
      title="Notification Analysis"
      renderSummary={renderNotificationResults}
    />
  );
};
